/**
 * API Utils - Frontend
 * @description Utility functions for API services
 */

// Helper function to clean undefined values from objects
export const cleanUndefinedValues = <T>(obj: T): Partial<T> => {
  const cleanObj: Partial<T> = {};

  if (obj && typeof obj === 'object') {
    Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
      if (value !== undefined) {
        (cleanObj as Record<string, unknown>)[key] = value;
      }
    });
  }

  return cleanObj;
};

// Helper function to clean undefined values recursively (for nested objects)
export const cleanUndefinedValuesRecursive = <T>(obj: T): Partial<T> => {
  const cleanObj: Partial<T> = {};

  if (obj && typeof obj === 'object') {
    Object.entries(obj as Record<string, unknown>).forEach(([key, value]) => {
      if (value !== undefined) {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Recursively clean nested objects
          const cleanedNested = cleanUndefinedValuesRecursive(value);
          if (Object.keys(cleanedNested).length > 0) {
            (cleanObj as Record<string, unknown>)[key] = cleanedNested;
          }
        } else {
          (cleanObj as Record<string, unknown>)[key] = value;
        }
      }
    });
  }

  return cleanObj;
};

// Helper function to strip MongoDB-specific fields
export const stripMongoFields = <T extends Record<string, unknown>>(
  obj: T
): Omit<T, '_id' | 'createdAt' | 'updatedAt' | '__v'> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { _id, createdAt, updatedAt, __v, ...cleanData } = obj;
  return cleanData as Omit<T, '_id' | 'createdAt' | 'updatedAt' | '__v'>;
};

// Helper function to handle API errors
export const handleApiError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const apiError = error as { response?: { data?: { error?: string } } };
    return apiError.response?.data?.error || 'An error occurred';
  }
  return 'An unexpected error occurred';
};
